import { ISbStoriesParams, getStoryblokApi } from '@storyblok/react';
import { errorWrapper } from 'commerce/utils/network';
import { getStoriesUrl, sbParams } from './config';
import { VERSION } from './types';

export const getStoryByIds = async ({
  uuids,
  language,
  isEditMode,
}: {
  uuids: string[];
  language: string;
  isEditMode?: boolean;
}) => {
  const storyblokApi = getStoryblokApi();
  const params = { ...sbParams };

  params.version = isEditMode ? VERSION.DRAFT : params.version;

  return !uuids?.length
    ? null
    : errorWrapper(getStoryByIds.name, () =>
        storyblokApi.getStories({
          ...params,
          language,
          by_uuids_ordered: uuids.join(','),
        }),
      );
};

export const getStoryBySlug = async ({
  slug,
  language,
  isEditMode,
}: {
  slug: string;
  language: string;
  isEditMode?: boolean;
}) => {
  const storyblokApi = getStoryblokApi();
  const params = { ...sbParams };

  params.version = isEditMode ? VERSION.DRAFT : params.version;

  return errorWrapper(getStoryBySlug.name, () =>
    storyblokApi.get(getStoriesUrl(slug), {
      ...params,
      language,
    }),
  );
};

const getStoriesBySlugs = async (
  slugs: string[],
  language: string,
  params?: ISbStoriesParams,
) => {
  const storyblokApi = getStoryblokApi();

  return errorWrapper(getStoriesBySlugs.name, () =>
    storyblokApi.getStories({
      // not possible to get search params in layout.ts
      ...sbParams,
      by_slugs: slugs.join(','),
      language,
      ...params,
    }),
  );
};

export const getSiteConfigStory = async (language: string) =>
  getStoryBySlug({ slug: 'configuration/site', language });

export const getConfigStories = (language: string) =>
  getStoriesBySlugs(
    [
      'configuration/header',
      'configuration/footer',
      'configuration/colors',
      'configuration/shipping',
      'configuration/materialInfo',
      'configuration/productdetail',
    ],
    language,
    { resolve_relations: 'NavigationItem.bannersSet' },
  );

export const getBannersTrustStories = (language: string) =>
  getStoryBySlug({
    slug: 'configuration/trust',
    language,
  });

export const getBannersReviewStories = (language: string) =>
  getStoryBySlug({
    slug: 'configuration/review-banner',
    language,
  });

export const getBrandStories = (language: string, page = 1) => {
  const storyblokApi = getStoryblokApi();

  return errorWrapper(getBrandStories.name, () =>
    storyblokApi.getStories({
      language,
      starts_with: 'marken/',
      content_type: 'page',
      per_page: 100,
      level: 2,
      page,
    }),
  );
};

export const getBannersUpsellingStories = (language: string) =>
  getStoryBySlug({
    slug: 'configuration/upselling-banners',
    language,
  });

export const getCategoriesForSearchStories = (language: string) => {
  const storyblokApi = getStoryblokApi();

  return errorWrapper(getCategoriesForSearchStories.name, () =>
    storyblokApi.getStories({
      language,
      filter_query: {
        showOnSearchPage: {
          in: true,
        },
      },
    }),
  );
};

export const getStoriesByPrefix = (language: string, slug: string) => {
  const storyblokApi = getStoryblokApi();

  return errorWrapper(getStoriesBySlugs.name, () =>
    storyblokApi.getStories({
      // not possible to get search params in layout.ts
      ...sbParams,
      by_slugs: `${slug}/*`,
      language,
    }),
  );
};
